import React from "react";
import { Dialog, DialogContent } from "@mui/material";
import { nanoid } from "nanoid";
import { parseTemplate } from "url-template";
import { urlFunctions } from "../../config/config.tooltip";

// helper function that generates link
const createLink = ({ field, site, classes, center = true }) => {
  const urlTemplate = parseTemplate(field.urlTemplate);
  let urlMapping = {};
  field.templateFields.forEach((templateField) => {
    // if template field exists in dataset, pull directly
    if (typeof templateField === "string") {
      urlMapping[templateField] = site[templateField];
    }
    // otherwise templateField is an object with properties:
    //  sourceField      // the field in the data
    //  destinationField // the field in the url template
    //  lookupFunction   // key to function in config.tooltip.js
    //    that maps the sourceField to destinationField
    else if (typeof templateField === "object") {
      urlMapping[templateField.destinationField] = urlFunctions[
        templateField.lookupFunction
      ](site[templateField.sourceField]);
    }
  });
  // if the entire url is passed as a template parameter (e.g. template
  //  looks like: {url}), then urlTemplate.expand returns an encoded uri
  const expandedUrl = decodeURIComponent(urlTemplate.expand(urlMapping));

  if (expandedUrl && expandedUrl !== "") {
    return (
      <div
        key={nanoid()}
        className={`${classes["link"]} ${center ? classes["center"] : ""}`}
      >
        <a href={expandedUrl} target="_blank" rel="noopener noreferrer">
          {site.hasOwnProperty(field.dynamicLabel)
            ? site[field.dynamicLabel] + " "
            : field.label + " "}

          <i className="fas fa-external-link-alt"></i>
        </a>
      </div>
    );
  }
};

export const renderTooltipFieldbySiteId = ({
  field,
  layerId,
  siteId,
  classes,
  dataSlice,
  layerState,
  handleToggleNWSModal,
  showNWSModal,
}) => {
  const dataName = layerState[layerId].data;
  let obj;

  if (dataSlice[dataName].data !== null) {
    const site = dataSlice[dataName].data.features.find((feat) => {
      let id = feat.properties[layerState[layerId].siteNumberField];
      // if no id in properties, default to feature-level id
      id = id ? id : feat.id;
      return id === siteId;
    });
    if (site) {
      switch (field.type) {
        case "text":
        case "text-header":
          obj = (
            <p
              key={field.label}
              className={`${classes["text-header"]} ${classes["text-container"]}`}
            >
              <span className={classes["left"]}> {field.label} </span>
              <span className={classes["right"]}>
                {site.properties[field.fieldName]}
              </span>
            </p>
          );
          break;
        case "text-header-value-only":
          obj = (
            <p
              key={field.label}
              className={classes["text-header-value-only"]}
              style={{
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              {site.properties[field.fieldName]}
            </p>
          );
          break;
        case "text-nowrap":
          obj = (
            <p
              key={field.label}
              className={`${classes["text-header"]} ${classes["text-container"]}`}
            >
              <span className={classes["left"]}> {field.label} </span>
              <span
                className={`${classes["right"]} ${classes["ellipsis"]}`}
                data-text={site.properties[field.fieldName]}
              >
                {site.properties[field.fieldName]}
              </span>
            </p>
          );
          break;
        case "text-highlight":
          obj = (
            <p
              key={field.label}
              className={`${classes["text-highlight"]} ${classes["text-container"]}`}
            >
              <span className={classes["left"]}> {field.label} </span>
              <span className={classes["right"]}>
                {site.properties[field.fieldName]}
              </span>
            </p>
          );
          break;
        case "text-footer":
          obj = (
            <p
              key={field.label}
              className={`${classes["text-footer"]} ${classes["text-container"]}`}
            >
              <span className={classes["left"]}> {field.label} </span>
              <span className={classes["right"]}>
                {site.properties[field.fieldName]}
              </span>
            </p>
          );
          break;
        case "link":
          obj = createLink({ classes, field, site: site.properties });
          break;
        case "imageUrl":
          obj = (
            <React.Fragment key={field.label}>
              <img
                src={field.local ? field.src : site.properties[field.fieldName]}
                alt="Embedded graphic thumbnail"
                className={classes["embedded-content__thumbnail"]}
                onClick={handleToggleNWSModal}
                style={{ cursor: "pointer" }}
              ></img>
              {/* see https://medium.com/swlh/building-modals-in-react-64d92591f4b */}
              {showNWSModal && (
                <Dialog open={showNWSModal} onClose={handleToggleNWSModal}>
                  <DialogContent>
                    <img
                      src={
                        field.local
                          ? field.src
                          : site.properties[field.fieldName]
                      }
                      alt="Embedded graphic"
                      className={classes["embedded-content"]}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </React.Fragment>
          );
          break;
        case "list":
          if (site.properties[field.fieldName].length > 0) {
            obj = (
              <div className={classes["table-container"]} key={field.label}>
                {/* <p className={classes["list-title"]}>{field.label}</p> */}
                <table>
                  <tr className={classes["list-header"]}>
                    {field.listEntries.map((e) => {
                      return (
                        <th className={classes["list-header-item"]}>
                          {e.label}
                        </th>
                      );
                    })}
                  </tr>

                  {site.properties[field.fieldName].map((entry) => {
                    let showVisit = true;
                    return (
                      <tr className={classes["list-entry"]}>
                        {field.listEntries.map((e) => {
                          if (e.type === "link") {
                            return (
                              <td className={classes["list-value"]}>
                                {createLink({
                                  classes,
                                  field: e,
                                  site: entry,
                                  center: false,
                                })}
                              </td>
                            );
                          }
                          return (
                            showVisit && (
                              <td className={classes["list-value"]}>
                                {entry[e.fieldName]}
                              </td>
                            )
                          );
                        })}
                      </tr>
                    );
                  })}
                </table>
              </div>
            );
          }
          break;
        case "list-strings":
          obj = (
            <p
              key={field.label}
              className={classes["text-header"]}
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span className={classes["left"]}> {field.label} </span>
              <span className={classes["right"]}>
                {" "}
                {site.properties[field.fieldName].map((entry, index) => (
                  <span key={index}>{entry}</span>
                ))}{" "}
              </span>
            </p>
          );
          break;
        default:
          obj = <p>Invalid field</p>;
      }
    }
  }

  return obj;
};

// generates video for sites with HIVIS cameras
// HIVIS documentation: https://code.usgs.gov/advanced-technology-group/HIVIS/-/tree/master
export const generateMedia = ({
  siteId,
  mediaType,
  hivisData,
  classes,
  handleToggleHivisModal,
  showHivisImageModal
}) => {
  let siteInfo = hivisData.filter((site) => site.nwisId === siteId);
  if (siteInfo.length !== 0) {
    siteInfo = siteInfo[0];
    switch (mediaType) {
      case "image":
        // allow image to expand into a modal
        return (
          <>
            <img
              src={siteInfo.overlayDir + siteInfo.camId + "_newest.jpg"}
              alt={"HIVIS camera most recent frame"}
              className={classes["media"]}
              onClick={handleToggleHivisModal}
              style={{ cursor: "pointer" }}
            />
            {showHivisImageModal && (
              <Dialog
                open={showHivisImageModal}
                onClose={handleToggleHivisModal}
              >
                <DialogContent>
                  <img
                    src={siteInfo.overlayDir + siteInfo.camId + "_newest.jpg"}
                    alt={"HIVIS camera most recent frame"}
                    className={classes["embedded-content"]}
                  />
                </DialogContent>
              </Dialog>
            )}
          </>
        );
      case "video":
        return (
          <video autoPlay={true} controls={true} className={classes["media"]}>
            <source
              src={
                siteInfo.locus === "umid"
                  ? siteInfo.camId + "_" + 720 + ".mp4"
                  : siteInfo.tlDir + siteInfo.camId + "_720.mp4"
              }
              type="video/mp4"
            />
          </video>
        );
      default:
        return (
          <p style={{ textAlign: "center" }}>No media found for this site</p>
        );
    }
  } else {
    return <p style={{ textAlign: "center" }}>No media found for this site</p>;
  }
};
